import { useState, useEffect, useCallback } from "react";
import { ChatPage } from "@/components/ChatPage";
import { LoginPage } from "@/components/LoginPage";
import { PendingVerification } from "@/components/PendingVerification";
import { getToken, clearToken, bootstrapAccess, fetchMe, logout, isEmailPendingError, UserInfo } from "@/lib/api";
import { onAuthEventV2 } from "@/lib/auth-memory";

export default function App() {
  // H3: access lives in memory only — after reload/new tab it is absent
  // until bootstrap re-mints it from the HttpOnly session cookie.
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [bootstrapping, setBootstrapping] = useState<boolean>(() => !getToken());
  const [user, setUser] = useState<UserInfo | null>(null);
  const [emailPending, setEmailPending] = useState(false);

  const checkMe = useCallback((): Promise<UserInfo | null> => {
    return fetchMe()
      .then((u) => {
        setEmailPending(false);
        setUser(u);
        return u;
      })
      .catch((e) => {
        if (isEmailPendingError(e)) {
          // Token is valid but the account is held until an admin confirms
          // the email in RCC — show the waiting screen, keep the session.
          setEmailPending(true);
          return null;
        }
        clearToken();
        setTokenState(null);
        setEmailPending(false);
        return null;
      });
  }, []);

  // H3 bootstrap: page load / reload / new tab — mint access from the
  // HttpOnly cookie via /web/access (no rotation), then validate via me.
  useEffect(() => {
    if (getToken()) {
      void checkMe();
      return;
    }
    void bootstrapAccess().then((ok) => {
      setBootstrapping(false);
      if (ok) {
        setTokenState(getToken());
        void checkMe();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // H3 cross-tab: a logout in a sibling tab converges this tab; an
  // ACCESS_UPDATED from the leader refreshes our memory access state.
  useEffect(() => {
    return onAuthEventV2((ev) => {
      if (ev.type === 'LOGOUT') {
        setTokenState(null);
        setUser(null);
        setEmailPending(false);
      } else {
        setTokenState(getToken());
      }
    });
  }, []);

  const handleLogin = (t: string) => {
    setTokenState(t);
    setEmailPending(false);
    void checkMe();
  };

  const handleLogout = () => {
    // Server-side logout (best-effort) + local clear
    void logout();
    setTokenState(null);
    setUser(null);
    setEmailPending(false);
  };

  if (bootstrapping) {
    // Silent bootstrap in flight — do not flash the login form.
    return null;
  }

  if (!token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (emailPending) {
    return (
      <PendingVerification
        onRefresh={() => void checkMe()}
        onLogout={handleLogout}
      />
    );
  }

  // F2: ChatPage needs the authenticated user. Wait for fetchMe to resolve.
  if (!user) {
    return (
      <div className="h-screen bg-surface flex items-center justify-center text-secondary text-sm">
        Загрузка...
      </div>
    );
  }

  return <ChatPage onLogout={handleLogout} user={user} onUserUpdated={setUser} />;
}
